import { GatewayQuestionnaire } from "@conformite/gateway";
import { useWatch } from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import { useMemo, useRef } from "react";
import { parseAnswerValue } from "../answerValue";

type QuestionDef = GatewayQuestionnaire.QuestionDefinitionResponse;

export function computeVisibleQuestionIds(
  questions: QuestionDef[],
  answers: Record<string, string>
): Set<string> {
  const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);
  const orderToId = new Map<number, string>();
  sortedQuestions.forEach((q) => orderToId.set(q.order, q.id));

  const ids = new Set<string>();

  sortedQuestions.forEach((q) => {
    if (!q.condition) {
      ids.add(q.id);
      return;
    }

    const { condition } = q;
    const groupResults = condition.groups.map((group) => {
      const ruleResults = group.rules.map((rule) => {
        const parentId = orderToId.get(rule.questionOrder);
        if (!parentId) return false;
        const currentValue = answers[parentId];
        if (!currentValue) return false;

        const selectedIds = parseAnswerValue(currentValue);
        return selectedIds.some((id) => rule.expectedOptionIds.includes(id));
      });

      const groupLogic = group.logic ?? "OR";
      return groupLogic === "AND"
        ? ruleResults.every(Boolean)
        : ruleResults.some(Boolean);
    });

    const topLogic = condition.logic ?? "AND";
    const isVisible =
      topLogic === "AND"
        ? groupResults.every(Boolean)
        : groupResults.some(Boolean);

    if (isVisible) {
      ids.add(q.id);
    }
  });

  return ids;
}

/**
 * Evaluates question visibility conditions against current form answers.
 * Must be used inside a FormProvider context.
 */
export function useVisibleQuestionIds(questions: QuestionDef[]): Set<string> {
  const formValues = useWatch() as Record<string, string>;
  const prevKeyRef = useRef("");
  const prevSetRef = useRef<Set<string>>(new Set());

  const visibleIds = useMemo(
    () => computeVisibleQuestionIds(questions, formValues),
    [questions, formValues]
  );

  const key = [...visibleIds].sort().join(",");
  if (key !== prevKeyRef.current) {
    prevKeyRef.current = key;
    prevSetRef.current = visibleIds;
  }

  return prevSetRef.current;
}
